import type {
  LoginCredentials,
  OAuthTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ValidateResetTokenResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  SpaAccountActivationRequest,
  SpaAccountActivationResponse,
} from '../types';
import { clearAuthStorage } from '../../../services/tokenService.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery.ts';
import {
  SPA_LOGIN_ENDPOINT,
  SPA_LOGOUT_ENDPOINT,
  SPA_RESET_PASSWORD_ENDPOINT,
  SPA_VALIDATE_RESET_TOKEN_ENDPOINT,
  SPA_CHANGE_PASSWORD_ENDPOINT,
  SPA_ACCOUNT_ACTIVATION_ENDPOINT,
} from '../constants';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    login: builder.mutation<OAuthTokenResponse, LoginCredentials>({
      query: (credentials) => {
        return {
          url: SPA_LOGIN_ENDPOINT,
          method: 'POST',
          body: credentials,
          credentials: 'include',
        };
      },
      transformErrorResponse: (response: any) => {
        return {
          status: response.status,
          message: mapAuthErrorMessage(response.status),
        };
      },
    }),
    requestPasswordReset: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (request) => {
        return {
          url: SPA_RESET_PASSWORD_ENDPOINT,
          method: 'POST',
          body: request,
          credentials: 'include',
        };
      },
      transformErrorResponse: (response: any) => {
        // Extract message from response if available
        if (response.status === 400 && response.data?.Message) {
          return {
            status: response.status,
            message: response.data.Message,
          };
        }
        return {
          status: response.status,
          message: mapResetPasswordErrorMessage(response.status),
        };
      },
    }),
    validateResetToken: builder.query<ValidateResetTokenResponse, string>({
      query: (token) => ({
        url: `${SPA_VALIDATE_RESET_TOKEN_ENDPOINT}?token=${encodeURIComponent(token)}`,
        method: 'GET',
        credentials: 'include',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        message: response.data?.ErrorMessage || 'Invalid or expired token.',
      }),
    }),
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (request) => ({
        url: SPA_CHANGE_PASSWORD_ENDPOINT,
        method: 'POST',
        body: request,
        credentials: 'include',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        message: response.data?.Message || 'An error occurred.',
      }),
    }),
    getAccountActivationContext: builder.query<SpaAccountActivationResponse, string>({
      query: (token) => ({
        url: SPA_ACCOUNT_ACTIVATION_ENDPOINT,
        method: 'POST',
        body: { operation: 'validate', token },
        credentials: 'include',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        message: response.data?.Message || 'Invalid or expired token.',
      }),
    }),
    processAccountActivation: builder.mutation<
      SpaAccountActivationResponse,
      SpaAccountActivationRequest
    >({
      query: (request) => ({
        url: SPA_ACCOUNT_ACTIVATION_ENDPOINT,
        method: 'POST',
        body: request,
        credentials: 'include',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        message: response.data?.Message || 'Account setup failed.',
      }),
    }),
  }),
});

export const logout = (): void => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  void fetch(`${baseUrl}${SPA_LOGOUT_ENDPOINT}`, {
    method: 'POST',
    credentials: 'include',
  });
  clearAuthStorage();
};

const mapAuthErrorMessage = (status: number) => {
  switch (status) {
    case 400:
    case 401:
      return 'Invalid credentials';
    case 500:
      return 'Server error';
    default:
      return 'Something went wrong';
  }
};

const mapResetPasswordErrorMessage = (status: number) => {
  switch (status) {
    case 400:
      return 'Invalid email address';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return 'An error occurred. Please try again.';
  }
};

export const {
  useLoginMutation,
  useRequestPasswordResetMutation,
  useValidateResetTokenQuery,
  useChangePasswordMutation,
  useGetAccountActivationContextQuery,
  useProcessAccountActivationMutation,
} = authApi;
export const {
  endpoints: {
    login,
    requestPasswordReset,
    validateResetToken,
    changePassword,
    getAccountActivationContext,
    processAccountActivation,
  },
  reducerPath,
  reducer,
  middleware,
} = authApi;
